import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Category, SchoolType } from "../../../../../domain/model/Category";
import { GetCategoryUseCase } from "../../../../../domain/usecase/category/get/GetUseCase";
import { State } from "../../../../../domain/model/ResponseState";
import { CreateCategoryUseCase } from "../../../../../domain/usecase/category/create/CreateUseCase";
import { DeleteCategoryUseCase } from "../../../../../domain/usecase/category/delete/DeleteUseCase";
import { UpdateCategoryUseCase } from "../../../../../domain/usecase/category/update/UpdateUseCase";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../../../data/constant/RouteConstants";
import { UploadFileUseCase } from "../../../../../domain/usecase/uploadFile/UploadFileUseCase";
import { Attachment } from "../../../../../domain/model/Attachment";
import Compressor from "compressorjs";
import { showGlobalLoadingView } from "../../../../../core/utils/Utils";

export function CategoryDetailsViewModel(
  getCategoryUseCase: GetCategoryUseCase,
  createCategoryUseCase: CreateCategoryUseCase,
  deleteCategoryUseCase: DeleteCategoryUseCase,
  updateCategoryUseCase: UpdateCategoryUseCase,
  uploadFileUseCase: UploadFileUseCase,
) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [category, setCategory] = useState<Category | undefined>({
    title: "",
    imageUrl: "",
    schoolTypes: [SchoolType.PRIMARY],
  });

  async function fetchCategory(id: string) {
    setLoading(true);
    const response = await getCategoryUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        setCategory(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  async function deleteCategory() {
    if (!category?._id) return;

    showGlobalLoadingView(true);
    const response = await deleteCategoryUseCase.invoke(category?._id);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_CATEGORIES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function saveCategory() {
    if (!category?._id) return;

    if (!category.title) {
      showSnackbar("You should enter a title");
      return;
    }

    showGlobalLoadingView(true);

    if (category.imageFile) {
      const attachment = await uploadImage(category.imageFile);
      if (!attachment) {
        showGlobalLoadingView(false);
        return;
      }
      category.imageUrl = attachment.downloadUrl;
    }

    const response = await updateCategoryUseCase.invoke(category);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_CATEGORIES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function createCategory() {
    if (!category) return;
    if (!category.imageFile) {
      showSnackbar("You should choose an image");
      return;
    }
    if (!category.title) {
      showSnackbar("You should enter a title");
      return;
    }
    showGlobalLoadingView(true);

    const attachment = await uploadImage(category.imageFile);
    if (!attachment) {
      showGlobalLoadingView(false);
      return;
    }
    category.imageUrl = attachment.downloadUrl;

    const response = await createCategoryUseCase.invoke(category);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_CATEGORIES);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    showGlobalLoadingView(false);
  }

  async function uploadImage(file: File): Promise<Attachment | undefined> {
    const compressedFile: File | Blob = await new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.8,
        maxHeight: 1800,
        maxWidth: 1800,
        success(result) {
          resolve(result);
        },
        error(error) {
          reject(error);
        },
      });
    });
    const formData = new FormData();
    formData.append("file", compressedFile);
    const response = await uploadFileUseCase.invoke(formData);
    switch (response.responseState) {
      case State.Success:
        return response.data;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function setImageUrl(file: File) {
    if (!file) return;
    setCategory({
      ...category,
      imageUrl: URL.createObjectURL(file),
      imageFile: file,
    });
  }

  function setTitle(title: string) {
    setCategory({
      ...category,
      title: title,
    });
  }
  function setSchoolTypes(value: SchoolType[] | string) {
    // 1. Handle the autofill safeguard right here
    // If it's a string, split it into an array. Otherwise, keep the array.
    const newSchoolTypes =
      typeof value === "string" ? (value.split(",") as SchoolType[]) : value;

    // 2. Update the state with the pluralized key
    setCategory({
      ...category,
      schoolTypes: newSchoolTypes,
    });
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error",
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    isLoading,
    category,
    fetchCategory,
    setImageUrl,
    setTitle,
    deleteCategory,
    saveCategory,
    createCategory,
    setSchoolTypes,
  };
}
